import type { Candle } from "../../types";

// T50 — Threshold-50 two-bar decision strategy. Native Fela R.I.O strategy with the same architectural status as CISD:
// deterministic, tick-data driven, no look-ahead, non-repainting. Bar 1 = setup, Bar 2 = decision (evaluated on its
// COMPLETED close). Bull: close>open && closeLoc ≥ t · Bear: close<open && closeLoc ≤ 1−t, closeLoc=(close−low)/(high−low).
// Entry = next available tick (Bar 3 open). 10-tick evaluation window (tick sequence, not seconds): MFE/MAE, target
// ladder (price points), next-bar failure → separate HEDGE event evaluated with the same window. Threshold is a config,
// 50% is the canonical starting hypothesis — research sweeps other thresholds on the same data (see T50Research).
export type Dir = "bull" | "bear";
export type T50State = "IDLE" | "PENDING" | "CONFIRMED" | "ACTIVE" | "SUCCESS" | "FAILED" | "HEDGED";

export interface T50Config {
  threshold: number; // canonical 0.50
  window: number; // ticks after entry
  targets: number[]; // price points, e.g. +20 … +60
  successTarget: number; // canonical +49
  minBarTicks: number; // a decision bar needs intra-bar range: 1s synthetic candles (1 tick = 1 candle) have closeLoc ∈ {0,1} → Tick-T50 mode instead
  microWindow: number; // Tick-T50: rolling micro-bar of N ticks (open = price N−1 ticks ago, H/L over the window, close = current tick)
  minMicroRange: number; // Tick-T50: ignore micro-bars whose range is below this (price points)
}
export const DEFAULT_T50_CONFIG: T50Config = { threshold: 0.5, window: 10, targets: [20, 25, 30, 35, 40, 45, 49, 50, 55, 60], successTarget: 49, minBarTicks: 2, microWindow: 5, minMicroRange: 0 };

/** Single-tick candles (1s synthetic: volume 1 everywhere) → the rolling Tick-T50 mode applies. */
export const isTickSeries = (candles: Candle[]) => candles.length > 20 && candles.slice(-20).every((c) => c.volume === 1);

/** Rolling micro-bar ending at tick i over the last N ticks (1 candle = 1 tick; close = tick price). */
export function microBar(candles: Candle[], i: number, n: number): Bar {
  const from = i - n + 1;
  let high = -Infinity, low = Infinity;
  for (let j = from; j <= i; j++) { const p = candles[j].close; if (p > high) high = p; if (p < low) low = p; }
  return { time: candles[i].time, open: candles[from].close, high, low, close: candles[i].close };
}

/** Bars built from fewer than `minBarTicks` ticks cannot carry a close-location decision (unknown tick count = server candles → allowed). */
export const decidable = (c: Candle, cfg: T50Config) => !(cfg.minBarTicks > 1 && c.volume > 0 && c.volume < cfg.minBarTicks);

export interface Bar { time: number; open: number; high: number; low: number; close: number }
export interface TargetResult { target: number; hit: boolean; tick: number | null }
export interface Excursion { mfe: number; mfeTick: number; mae: number; maeTick: number; ticks: number; complete: boolean; targets: TargetResult[] }
export interface HedgeEvent extends Excursion { dir: Dir; entryIndex: number; entryPrice: number; entryTime: number; recovered: boolean }
export interface T50Event extends Excursion {
  id: string;
  mode: "bar" | "tick";
  microWindow?: number; // Tick-T50 only
  displacement?: number; // |body| / range of the decision (micro-)bar — recorded for research, not part of the rule
  dir: Dir;
  threshold: number;
  closeLoc: number;
  bar1Index: number;
  index: number; // Bar 2 (decision bar)
  bar1: Bar;
  bar2: Bar;
  time: number; // Bar 2 time
  state: T50State;
  entryIndex: number | null;
  entryPrice: number | null;
  entryTime: number | null;
  failed: boolean;
  failIndex: number | null;
  hedge: HedgeEvent | null;
  outcome: "SUCCESS" | "FAILED" | "NEUTRAL" | "PENDING";
}
export interface T50Pending { bar1Index: number; index: number; closeLoc: number | null; dir: Dir | null; wouldConfirm: boolean }
export interface T50Result { config: T50Config; events: T50Event[]; pending: T50Pending | null; state: T50State; last: T50Event | null; decidableBars: number; mode: "bar" | "tick" }

const bar = (c: Candle): Bar => ({ time: c.time, open: c.open, high: c.high, low: c.low, close: c.close });
const EMPTY_EXC = (targets: number[]): Excursion => ({ mfe: 0, mfeTick: 0, mae: 0, maeTick: 0, ticks: 0, complete: false, targets: targets.map((t) => ({ target: t, hit: false, tick: null })) });

export const closeLocation = (c: Bar): number | null => (c.high > c.low ? (c.close - c.low) / (c.high - c.low) : null);

/** Decision on a COMPLETED bar: bull / bear / null. Pure — the only place the threshold rule lives. */
export function decide(c: Bar, threshold: number): { dir: Dir; closeLoc: number } | null {
  const loc = closeLocation(c);
  if (loc == null) return null; // zero-range candle: no decision
  if (c.close > c.open && loc >= threshold - 1e-12) return { dir: "bull", closeLoc: loc };
  if (c.close < c.open && loc <= 1 - threshold + 1e-12) return { dir: "bear", closeLoc: loc };
  return null;
}

/** Evaluate entry over the next `window` ticks (candles[entryIndex .. entryIndex+window-1] — the entry tick is tick 1). Uses only bars ≤ end. */
export function evaluate(candles: Candle[], entryIndex: number, entryPrice: number, dir: Dir, cfg: T50Config, limit = candles.length - 1): Excursion {
  const ex = EMPTY_EXC(cfg.targets);
  const sign = dir === "bull" ? 1 : -1;
  // Window = `cfg.window` SOURCE TICKS: walk bars from the entry accumulating their tick counts (volume); bars with an
  // unknown tick count (server candles, volume 0) count as one tick each. Tick indices reported are cumulative.
  let acc = 0;
  for (let j = entryIndex; j <= limit && acc < cfg.window; j++) {
    const c = candles[j], n = c.volume > 0 ? c.volume : 1;
    const k = Math.min(cfg.window, acc + n);
    const fav = sign * ((dir === "bull" ? c.high : c.low) - entryPrice), adv = sign * (entryPrice - (dir === "bull" ? c.low : c.high));
    if (fav > ex.mfe) { ex.mfe = fav; ex.mfeTick = k; }
    if (adv > ex.mae) { ex.mae = adv; ex.maeTick = k; }
    for (const t of ex.targets) if (!t.hit && fav >= t.target) { t.hit = true; t.tick = k; }
    acc = k; ex.ticks = k;
  }
  ex.complete = acc >= cfg.window;
  return ex;
}

export const T50Engine = {
  /** Batch analysis (O(n)); state at index i depends only on candles[0..i]. The last candle is treated as the forming bar (pending).
   *  Single-tick series (1s synthetic) are routed to the rolling Tick-T50 mode. */
  analyze(candles: Candle[], cfg: T50Config = DEFAULT_T50_CONFIG, opts: { forming?: boolean; mode?: "bar" | "tick" | "auto" } = {}): T50Result {
    const mode = opts.mode && opts.mode !== "auto" ? opts.mode : isTickSeries(candles) ? "tick" : "bar";
    if (mode === "tick") return T50Engine.analyzeTicks(candles, cfg, opts);
    const events: T50Event[] = [];
    const forming = opts.forming ?? true;
    const lastComplete = forming ? candles.length - 2 : candles.length - 1; // index of the last COMPLETED bar
    for (let i = 1; i <= lastComplete; i++) {
      const b2 = candles[i], d = decidable(b2, cfg) ? decide(b2, cfg.threshold) : null;
      if (!d) continue;
      const ev: T50Event = { ...EMPTY_EXC(cfg.targets), id: `t50-${b2.time}-${d.dir}`, mode: "bar", displacement: b2.high > b2.low ? Math.abs(b2.close - b2.open) / (b2.high - b2.low) : 0, dir: d.dir, threshold: cfg.threshold, closeLoc: d.closeLoc, bar1Index: i - 1, index: i,
        bar1: bar(candles[i - 1]), bar2: bar(b2), time: b2.time, state: "CONFIRMED", entryIndex: null, entryPrice: null, entryTime: null, failed: false, failIndex: null, hedge: null, outcome: "PENDING" };
      if (i + 1 <= lastComplete + (forming ? 1 : 0) && candles[i + 1]) { // entry at the next available tick (Bar 3 open)
        const e = candles[i + 1];
        ev.entryIndex = i + 1; ev.entryPrice = e.open; ev.entryTime = e.time; ev.state = "ACTIVE";
        Object.assign(ev, evaluate(candles, i + 1, e.open, d.dir, cfg, lastComplete));
        if (i + 1 <= lastComplete) { // next COMPLETED bar closes opposite → FAILED → hedge signal
          const nb = candles[i + 1], opp = d.dir === "bull" ? nb.close < nb.open : nb.close > nb.open;
          if (opp) {
            ev.failed = true; ev.failIndex = i + 1; ev.state = "FAILED";
            const hDir: Dir = d.dir === "bull" ? "bear" : "bull";
            if (candles[i + 2] && i + 2 <= lastComplete + (forming ? 1 : 0)) {
              const he = candles[i + 2];
              const hx = evaluate(candles, i + 2, he.open, hDir, cfg, lastComplete);
              ev.hedge = { ...hx, dir: hDir, entryIndex: i + 2, entryPrice: he.open, entryTime: he.time, recovered: hx.targets.some((t) => t.target === cfg.successTarget && t.hit) };
              ev.state = "HEDGED";
            }
          }
        }
        const win = ev.targets.find((t) => t.target === cfg.successTarget)?.hit ?? false;
        if (ev.failed) ev.outcome = "FAILED";
        else if (win) { ev.outcome = "SUCCESS"; ev.state = "SUCCESS"; }
        else if (ev.complete) ev.outcome = "NEUTRAL";
      }
      events.push(ev);
    }
    let pending: T50Pending | null = null;
    if (forming && candles.length >= 2) {
      const f = candles[candles.length - 1], d = decidable(f, cfg) ? decide(f, cfg.threshold) : null;
      pending = { bar1Index: candles.length - 2, index: candles.length - 1, closeLoc: closeLocation(f), dir: d?.dir ?? null, wouldConfirm: !!d };
    }
    const last = events[events.length - 1] ?? null;
    const decidableBars = candles.reduce((n, c) => n + (decidable(c, cfg) ? 1 : 0), 0);
    const state: T50State = last && last.index >= lastComplete - 1 && last.state !== "SUCCESS" ? last.state : pending ? "PENDING" : last ? last.state : "IDLE";
    return { config: cfg, events, pending, state, last, decidableBars, mode: "bar" };
  },

  /** Rolling Tick-T50 (1s chart): every tick is a decision point over an N-tick micro-bar; Micro-Bar 1 = the previous rolling
   *  structure. State gating: an event fires only on a false→true transition of the condition AND only when no lifecycle is
   *  running (entry + `window` raw ticks). Entry = next tick; the outcome window is the following `window` RAW ticks. */
  analyzeTicks(candles: Candle[], cfg: T50Config = DEFAULT_T50_CONFIG, opts: { forming?: boolean } = {}): T50Result {
    const events: T50Event[] = [];
    const n = Math.max(2, cfg.microWindow || 5);
    const forming = opts.forming ?? true;
    const lastComplete = forming ? candles.length - 2 : candles.length - 1; // the forming 1s candle is the live (incomplete) tick
    let prevDir: Dir | null = null, lockedUntil = -1;
    const qualify = (i: number) => {
      const mb = microBar(candles, i, n);
      if (mb.high - mb.low < Math.max(cfg.minMicroRange, 1e-12)) return { mb, d: null as null | { dir: Dir; closeLoc: number } };
      return { mb, d: decide(mb, cfg.threshold) };
    };
    for (let i = n; i <= lastComplete; i++) {
      const { mb, d } = qualify(i);
      const dir = d?.dir ?? null;
      const fresh = !!d && dir !== prevDir; // false → true transition (or direction flip)
      prevDir = dir;
      if (!d || !fresh || i <= lockedUntil) continue;
      const mb1 = microBar(candles, i - 1, n);
      const ev: T50Event = { ...EMPTY_EXC(cfg.targets), id: `t50t-${mb.time}-${d.dir}`, mode: "tick", microWindow: n, displacement: Math.abs(mb.close - mb.open) / (mb.high - mb.low),
        dir: d.dir, threshold: cfg.threshold, closeLoc: d.closeLoc, bar1Index: i - 1, index: i, bar1: mb1, bar2: mb, time: mb.time, state: "CONFIRMED",
        entryIndex: null, entryPrice: null, entryTime: null, failed: false, failIndex: null, hedge: null, outcome: "PENDING" };
      if (candles[i + 1] && i + 1 <= lastComplete + (forming ? 1 : 0)) {
        const e = candles[i + 1];
        ev.entryIndex = i + 1; ev.entryPrice = e.close; ev.entryTime = e.time; ev.state = "ACTIVE";
        Object.assign(ev, evaluate(candles, i + 2, e.close, d.dir, cfg, lastComplete));
        lockedUntil = i + 1 + cfg.window; // lifecycle: entry tick + `window` raw ticks
        if (i + 1 <= lastComplete) { // failure = the NEXT micro-bar decides the opposite direction
          const nd = qualify(i + 1).d;
          if (nd && nd.dir !== d.dir) {
            ev.failed = true; ev.failIndex = i + 1; ev.state = "FAILED";
            const hDir: Dir = nd.dir;
            if (candles[i + 2] && i + 2 <= lastComplete + (forming ? 1 : 0)) {
              const he = candles[i + 2];
              const hx = evaluate(candles, i + 3, he.close, hDir, cfg, lastComplete);
              ev.hedge = { ...hx, dir: hDir, entryIndex: i + 2, entryPrice: he.close, entryTime: he.time, recovered: hx.targets.some((t) => t.target === cfg.successTarget && t.hit) };
              ev.state = "HEDGED";
            }
          }
        }
        const win = ev.targets.find((t) => t.target === cfg.successTarget)?.hit ?? false;
        if (ev.failed) ev.outcome = "FAILED";
        else if (win) { ev.outcome = "SUCCESS"; ev.state = "SUCCESS"; }
        else if (ev.complete) ev.outcome = "NEUTRAL";
      }
      events.push(ev);
    }
    let pending: T50Pending | null = null;
    if (forming && candles.length > n) {
      const i = candles.length - 1, { mb, d } = qualify(i);
      pending = { bar1Index: i - 1, index: i, closeLoc: closeLocation(mb), dir: d?.dir ?? null, wouldConfirm: !!d && d.dir !== prevDir && i > lockedUntil };
    }
    const last = events[events.length - 1] ?? null;
    const state: T50State = last && last.state === "ACTIVE" ? "ACTIVE" : pending ? "PENDING" : last ? last.state : "IDLE";
    return { config: cfg, events, pending, state, last, decidableBars: Math.max(0, lastComplete - n + 1), mode: "tick" };
  },
};
